import type { ChangeEvent, CompositionEvent, FormEvent, KeyboardEvent, ReactNode } from "react";
import { useId, useRef } from "react";
import { Link } from "react-router-dom";

type AdminListToolbarProps = {
  title: string;
  totalCount: number;
  draftQuery: string;
  appliedQuery: string;
  searchPlaceholder: string;
  csvButtonLabel: string;
  csvDisabled?: boolean;
  searchDisabled?: boolean;
  createHref?: string;
  createLabel?: string;
  children?: ReactNode;
  onDraftQueryChange: (value: string) => void;
  onSearchSubmit: () => void;
  onExportCsv: () => void;
};

export function AdminListToolbar({
  title,
  totalCount,
  draftQuery,
  appliedQuery,
  searchPlaceholder,
  csvButtonLabel,
  csvDisabled = false,
  searchDisabled = false,
  createHref,
  createLabel,
  children,
  onDraftQueryChange,
  onSearchSubmit,
  onExportCsv
}: AdminListToolbarProps) {
  const searchInputId = useId();
  const isComposingRef = useRef(false);
  const hasUnsubmittedQuery = draftQuery.trim() !== appliedQuery;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isComposingRef.current || searchDisabled) {
      return;
    }

    onSearchSubmit();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter") {
      return;
    }

    if (isComposingRef.current || event.nativeEvent.isComposing) {
      event.preventDefault();
    }
  }

  function handleCompositionStart(_event: CompositionEvent<HTMLInputElement>) {
    isComposingRef.current = true;
  }

  function handleCompositionEnd(_event: CompositionEvent<HTMLInputElement>) {
    isComposingRef.current = false;
  }

  function handleDraftChange(event: ChangeEvent<HTMLInputElement>) {
    onDraftQueryChange(event.target.value);
  }

  return (
    <section className="admin-list-toolbar" aria-label={`${title}の検索と操作`}>
      <div className="admin-list-toolbar__header">
        <div>
          <h2>{title}</h2>
          <p>絞り込み結果 {totalCount}件</p>
        </div>
        <div className="admin-list-toolbar__actions">
          {createHref && createLabel ? <Link className="admin-list-button admin-list-button--primary" to={createHref}>{createLabel}</Link> : null}
          <button type="button" className="admin-list-button" disabled={csvDisabled} onClick={onExportCsv}>
            {csvButtonLabel}
          </button>
        </div>
      </div>
      <form className="admin-list-search" role="search" onSubmit={handleSubmit}>
        <label className="admin-list-search__label" htmlFor={searchInputId}>
          検索
        </label>
        <div className="admin-list-search__controls">
          <input
            id={searchInputId}
            type="search"
            value={draftQuery}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            inputMode="search"
            enterKeyHint="search"
            placeholder={searchPlaceholder}
          />
          <button type="submit" className="admin-list-button admin-list-search__button" disabled={searchDisabled}>
            検索
          </button>
        </div>
        {hasUnsubmittedQuery ? (
          <p className="admin-list-search__hint" aria-live="polite">
            検索ボタンを押すと反映されます。
          </p>
        ) : null}
      </form>
      {children}
    </section>
  );
}
